import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { addSymptom, getSymptoms, deleteSymptom, updateSymptom } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { ClearableInput } from '../components/ui/clearable-input';
import { toast } from 'sonner';
import { VoiceInput } from '../components/VoiceInput';
import { HealthCorrelations } from '../components/HealthCorrelations';
import SymptomCalendar from '../components/SymptomCalendar';
import MoodCalendar from '../components/MoodCalendar';
import MoodSymptomCorrelation from '../components/MoodSymptomCorrelation';
import BodyCompositionTracker from '../components/BodyCompositionTracker';
import LabValuesTracker from '../components/LabValuesTracker';
import SymptomAnalytics from '../components/SymptomAnalytics';
import PredictionHistory from '../components/PredictionHistory';
import { HealthAlerts } from '../components/HealthAlerts';
import { HealthTrendSection } from '../components/HealthTrendSection';
import { CalendarStrip } from '../components/CalendarStrip';
import { useLanguage } from '../contexts/LanguageContext';
import { SymptomHistoryList } from '../components/health/SymptomHistoryList';
import { SYMPTOM_CATEGORIES as categories, getCategoryInfo } from '../lib/symptomCategories';
import { useSmartComment } from '../hooks/useSmartComment';
import { MemorySuggestions } from '../components/MemorySuggestions';
import { MedicationsSection } from '../components/MedicationsSection';
import { LifeClock } from '../components/LifeClock';
import { BodyTracker } from '../components/BodyTracker';
import { useAuth } from '../contexts/AuthContext';
import {
    HeartPulse, Plus, Trash2, Brain, Frown, Zap, Activity,
    Wind, Droplets, Pencil, Check, X, ChevronDown, Loader2, AlertCircle,
    Stethoscope, Leaf, Bone, Sparkles, Eye, Calendar, FileText, Flower2, Download
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Health() {
    const { t, language } = useLanguage();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const bodyTrackerRef = useRef(null);
    const [bodyHighlight, setBodyHighlight] = useState(false);
    const [symptoms, setSymptoms] = useState([]);
    const [allSymptoms, setAllSymptoms] = useState([]);
    const [allSymptomsLoading, setAllSymptomsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [quickLogOpen, setQuickLogOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    
    // Quick log form
    const [selectedCategory, setSelectedCategory] = useState('head');
    const [selectedSymptom, setSelectedSymptom] = useState('headache');
    const [customSymptom, setCustomSymptom] = useState('');
    const [severity, setSeverity] = useState(5);
    const [notes, setNotes] = useState('');
    const [notesInterim, setNotesInterim] = useState('');
    const [quickLoading, setQuickLoading] = useState(false);
    const { fetchComment } = useSmartComment();
    
    // Treatments
    const [treatments, setTreatments] = useState([]);
    const [treatmentDrawerOpen, setTreatmentDrawerOpen] = useState(false);
    const [treatmentForm, setTreatmentForm] = useState({ treatment_type: 'doctor', title: '', provider: '', notes: '' });
    const [treatmentLoading, setTreatmentLoading] = useState(false);
    const [showTreatments, setShowTreatments] = useState(true);

    const treatmentTypes = [
        { value: 'doctor', label: { de: 'Arztbesuch', en: 'Doctor' }, icon: Stethoscope, color: '#3b82f6' },
        { value: 'tcm', label: { de: 'TCM', en: 'TCM' }, icon: Leaf, color: '#16a34a' },
        { value: 'physio', label: { de: 'Physio', en: 'Physio' }, icon: Bone, color: '#f97316' },
        { value: 'dentist', label: { de: 'Zahnarzt', en: 'Dentist' }, icon: Sparkles, color: '#8b5cf6' },
        { value: 'eye', label: { de: 'Augenarzt', en: 'Eye' }, icon: Eye, color: '#06b6d4' },
        { value: 'mental', label: { de: 'Psychologe', en: 'Psych' }, icon: Brain, color: '#ec4899' },
        { value: 'lab', label: { de: 'Labor', en: 'Lab' }, icon: FileText, color: '#eab308' },
        { value: 'other', label: { de: 'Andere', en: 'Other' }, icon: HeartPulse, color: '#6b7280' },
    ];


    const symptomsByCategory = {
        head: [
            { value: 'headache', label: { de: 'Kopfschmerzen', en: 'Headache' } },
            { value: 'migraine', label: { de: 'Migräne', en: 'Migraine' } },
            { value: 'dizziness', label: { de: 'Schwindel', en: 'Dizziness' } },
        ],
        stomach: [
            { value: 'stomach_ache', label: { de: 'Bauchschmerzen', en: 'Stomach ache' } },
            { value: 'nausea', label: { de: 'Übelkeit', en: 'Nausea' } },
            { value: 'bloating', label: { de: 'Blähungen', en: 'Bloating' } },
        ],
        energy: [
            { value: 'fatigue', label: { de: 'Müdigkeit', en: 'Fatigue' } },
            { value: 'exhaustion', label: { de: 'Erschöpfung', en: 'Exhaustion' } },
        ],
        back: [
            { value: 'back_pain', label: { de: 'Rückenschmerzen', en: 'Back pain' } },
            { value: 'neck_pain', label: { de: 'Nackenschmerzen', en: 'Neck pain' } },
        ],
        mood: [
            { value: 'anxiety', label: { de: 'Angst', en: 'Anxiety' } },
            { value: 'stress', label: { de: 'Stress', en: 'Stress' } },
            { value: 'sad', label: { de: 'Traurig', en: 'Sad' } },
        ],
        respiratory: [
            { value: 'cough', label: { de: 'Husten', en: 'Cough' } },
            { value: 'congestion', label: { de: 'Verstopfung', en: 'Congestion' } },
        ],
        other: [
            { value: 'general_pain', label: { de: 'Schmerzen', en: 'Pain' } },
            { value: 'swelling', label: { de: 'Schwellung', en: 'Swelling' } },
            { value: 'dizziness', label: { de: 'Schwindel', en: 'Dizziness' } },
            { value: 'other', label: { de: 'Sonstiges', en: 'Other' } },
        ],
        recovery: [
            { value: 'muscle_soreness', label: { de: 'Muskelkater', en: 'Muscle soreness' } },
            { value: 'fatigue', label: { de: 'Erschöpfung', en: 'Fatigue' } },
            { value: 'relaxed', label: { de: 'Entspannt', en: 'Relaxed' } },
            { value: 'recovered', label: { de: 'Erholt', en: 'Recovered' } },
        ],
        wellness: [
            { value: 'meditation', label: { de: 'Meditation', en: 'Meditation' } },
            { value: 'sauna', label: { de: 'Sauna', en: 'Sauna' } },
            { value: 'whirlpool', label: { de: 'Whirlpool', en: 'Whirlpool' } },
            { value: 'massage', label: { de: 'Massage', en: 'Massage' } },
            { value: 'cold_bath', label: { de: 'Eisbad / Kalt', en: 'Ice Bath / Cold' } },
            { value: 'breathing', label: { de: 'Atemübung', en: 'Breathing' } },
            { value: 'forest_bath', label: { de: 'Waldbaden', en: 'Forest Bathing' } },
            { value: 'spa', label: { de: 'Spa / Therme', en: 'Spa / Hot Spring' } },
        ],
    };

    useEffect(() => {
        loadSymptoms();
        loadTreatments();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loaders are stable closures
    }, [selectedDate]);

    // Load full chronological symptom list (last 365 days) for "Symptom-Verlauf"
    useEffect(() => {
        loadAllSymptoms();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
    }, []);

    useEffect(() => {
        const syms = symptomsByCategory[selectedCategory];
        if (syms && syms.length > 0) {
            setSelectedSymptom(syms[0].value);
        }
        setCustomSymptom('');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- symptomsByCategory is module-scope constant
    }, [selectedCategory]);

    // Handle ?focus=body — scroll & highlight BodyTracker on landing
    useEffect(() => {
        if (searchParams.get('focus') === 'body') {
            const t = setTimeout(() => {
                if (bodyTrackerRef.current) {
                    bodyTrackerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setBodyHighlight(true);
                    setTimeout(() => setBodyHighlight(false), 2500);
                }
            }, 400);
            return () => clearTimeout(t);
        }
    }, [searchParams]);

    const loadSymptoms = async () => {
        try {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const response = await getSymptoms(dateStr);
            setSymptoms(response.data);
        } catch (error) {
            console.error('Failed to load symptoms:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAllSymptoms = async () => {
        setAllSymptomsLoading(true);
        try {
            // No date filter -> backend returns last `days` (365) of symptoms, sorted desc
            const response = await getSymptoms(undefined, 365);
            setAllSymptoms(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Failed to load all symptoms:', error);
        } finally {
            setAllSymptomsLoading(false);
        }
    };

    const loadTreatments = async () => {
        try {
            const res = await axios.get(`${API}/api/health/treatments?days=90`);
            setTreatments(Array.isArray(res.data) ? res.data : []);
        } catch (e) { console.error('Treatments error:', e); }
    };

    const handleSaveTreatment = async () => {
        if (!treatmentForm.title.trim()) return;
        setTreatmentLoading(true);
        try {
            await axios.post(`${API}/api/health/treatments`, {
                ...treatmentForm,
                logged_at: selectedDate.toISOString()
            });
            toast.success(`${language === 'de' ? 'Behandlung gespeichert' : 'Treatment saved'} +5 XP`);
            setTreatmentDrawerOpen(false);
            setTreatmentForm({ treatment_type: 'doctor', title: '', provider: '', notes: '' });
            loadTreatments();
            fetchComment('health', `${treatmentForm.treatment_type}: ${treatmentForm.title}`, language);
        } catch (e) { toast.error('Error'); }
        finally { setTreatmentLoading(false); }
    };

    const handleDeleteTreatment = async (id) => {
        try {
            await axios.delete(`${API}/api/health/treatments/${id}`);
            toast.success(language === 'de' ? 'Gelöscht' : 'Deleted');
            loadTreatments();
        } catch (e) { toast.error('Error'); }
    };

    const handleQuickLog = async () => {
        if (!selectedSymptom && !customSymptom.trim()) return;
        setQuickLoading(true);
        try {
            const symptomLabel = customSymptom.trim() || symptomsByCategory[selectedCategory]?.find(s => s.value === selectedSymptom)?.label[language] || selectedSymptom;
            await addSymptom({
                category: selectedCategory,
                symptom: symptomLabel,
                severity: severity,
                notes: notes || null,
                logged_at: selectedDate.toISOString()
            });
            toast.success(language === 'de' ? '📋 Symptom gespeichert! +3 XP' : '📋 Symptom saved! +3 XP');
            setQuickLogOpen(false);
            resetForm();
            loadSymptoms();
            loadAllSymptoms();
            fetchComment('health', `${symptomLabel} (Schwere ${severity}/10)`, language);
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        } finally {
            setQuickLoading(false);
        }
    };

    const handleExportSymptomsCSV = () => {
        if (!allSymptoms || allSymptoms.length === 0) {
            toast.info(language === 'de' ? 'Keine Symptome zum Exportieren' : 'No symptoms to export');
            return;
        }
        // Build CSV (RFC 4180-ish, UTF-8 with BOM for Excel)
        const headers = language === 'de'
            ? ['Datum', 'Uhrzeit', 'Symptom', 'Kategorie', 'Schwere', 'Notizen']
            : ['Date', 'Time', 'Symptom', 'Category', 'Severity', 'Notes'];
        const esc = (v) => {
            const s = v == null ? '' : String(v);
            if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };
        // Sort ascending chronologically for the export
        const rows = [...allSymptoms]
            .sort((a, b) => String(a.logged_at || '').localeCompare(String(b.logged_at || '')))
            .map((e) => {
                const raw = e.logged_at;
                const dt = raw ? (typeof raw === 'string' ? parseISO(raw) : raw) : new Date();
                const date = format(dt, 'yyyy-MM-dd');
                const time = format(dt, 'HH:mm');
                const cat = getCategoryInfo(e.category).label[language] || e.category || '';
                return [date, time, e.symptom || '', cat, e.severity ?? '', e.notes || ''].map(esc).join(',');
            });
        const csv = '\ufeff' + headers.join(',') + '\n' + rows.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const today = format(new Date(), 'yyyy-MM-dd');
        a.download = `bestbuddy_symptome_${today}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(language === 'de' ? '📥 CSV exportiert' : '📥 CSV exported');
    };

    const handleDeleteSymptom = async (id) => {
        try {
            await deleteSymptom(id);
            toast.success(language === 'de' ? 'Gelöscht' : 'Deleted');
            loadSymptoms();
            loadAllSymptoms();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        }
    };

    const handleUpdateSymptom = async (id, data) => {
        try {
            await updateSymptom(id, data);
            toast.success(language === 'de' ? 'Aktualisiert' : 'Updated');
            setEditingEntry(null);
            loadSymptoms();
            loadAllSymptoms();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler' : 'Error');
        }
    };

    const resetForm = () => {
        setSelectedCategory('head');
        setSelectedSymptom('headache');
        setSeverity(5);
        setNotes('');
    };

    const getSeverityColor = (sev) => sev <= 3 ? 'text-green-400' : sev <= 6 ? 'text-yellow-400' : 'text-red-400';
    const getSeverityBg = (sev) => sev <= 3 ? 'bg-green-500/20' : sev <= 6 ? 'bg-yellow-500/20' : 'bg-red-500/20';

    // Group symptoms by category
    const groupedSymptoms = symptoms.reduce((acc, sym) => {
        const cat = sym.category || 'general';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(sym);
        return acc;
    }, {});

    const totalSymptoms = symptoms.length;
    const avgSeverity = symptoms.length > 0 
        ? (symptoms.reduce((sum, s) => sum + (s.severity || 5), 0) / symptoms.length).toFixed(1)
        : 0;

    return (
        <div className="pb-24 lg:pb-8" data-testid="health-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-4"
            >
                {/* Sticky Header */}
                <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg -mx-4 px-4 pt-4 pb-3 border-b border-border">
                    <div className="flex items-center justify-between mb-3">
                        <h1 className="font-display text-xl lg:text-2xl font-bold flex items-center gap-2">
                            <HeartPulse className="w-6 h-6 icon-health" />
                            {language === 'de' ? 'Gesundheit' : 'Health'}
                        </h1>
                        <Button 
                            className="rounded-full h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90"
                            onClick={() => setQuickLogOpen(true)}
                            data-testid="add-symptom-btn"
                        >
                            <Plus className="w-5 h-5 mr-1" />
                            {language === 'de' ? 'Symptom' : 'Symptom'}
                        </Button>
                    </div>
                    
                    <CalendarStrip 
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                    />
                </div>

                <div className="px-4 space-y-4">
                    {/* Health Alerts */}
                    <HealthAlerts />

                    {/* Trend — symptom frequency by category over a chosen period */}
                    <HealthTrendSection />

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3">
                        <Card className="module-health border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="w-5 h-5 text-rose-400" />
                                    <span className="text-sm text-muted-foreground">{language === 'de' ? 'Symptome heute' : 'Symptoms today'}</span>
                                </div>
                                <p className="text-2xl font-bold text-foreground">{totalSymptoms}</p>
                            </CardContent>
                        </Card>
                        <Card className="module-fitness border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Activity className="w-5 h-5 text-amber-400" />
                                    <span className="text-sm text-muted-foreground">{language === 'de' ? 'Ø Schwere' : 'Avg Severity'}</span>
                                </div>
                                <p className="text-2xl font-bold text-foreground">{avgSeverity}/10</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* ==================== BODY TRACKER ==================== */}
                    <div
                        ref={bodyTrackerRef}
                        className={`rounded-2xl transition-all duration-500 ${bodyHighlight ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg' : ''}`}
                        data-testid="health-body-section"
                    >
                        <BodyTracker language={language} />
                    </div>

                    {/* ==================== MEDICATIONS SECTION ==================== */}
                    <MedicationsSection language={language} />

                    {/* ==================== LIFE CLOCK ==================== */}
                    <LifeClock language={language} birthDate={user?.birth_date || user?.birthdate || user?.date_of_birth} gender={user?.gender} />

                    {/* ==================== TREATMENTS SECTION ==================== */}
                    <Card className="border" data-testid="treatments-section">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-sm flex items-center gap-2">
                                    <Stethoscope className="w-4 h-4 text-blue-500" />
                                    {language === 'de' ? 'Behandlungen & Termine' : 'Treatments & Appointments'}
                                </h3>
                                <Button
                                    size="sm"
                                    onClick={() => setTreatmentDrawerOpen(true)}
                                    className="rounded-full h-8 px-3 bg-primary text-primary-foreground"
                                    data-testid="add-treatment-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    {language === 'de' ? 'Termin' : 'Add'}
                                </Button>
                            </div>
                            
                            {treatments.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                    {language === 'de' ? 'Keine Termine erfasst. Erfasse Arztbesuche, TCM, Physio etc.' : 'No treatments logged yet.'}
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {treatments.slice(0, showTreatments ? 10 : 3).map((t) => {
                                        const typeInfo = treatmentTypes.find(tt => tt.value === t.treatment_type) || treatmentTypes[7];
                                        const TypeIcon = typeInfo.icon;
                                        const loggedAt = t.logged_at ? parseISO(t.logged_at) : new Date();
                                        return (
                                            <div key={t.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl group">
                                                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: typeInfo.color + '20' }}>
                                                    <TypeIcon className="w-4 h-4" style={{ color: typeInfo.color }} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">{t.title}</p>
                                                    <p className="text-[11px] text-muted-foreground">
                                                        {format(loggedAt, 'd. MMM yyyy', { locale: language === 'de' ? de : enUS })}
                                                        {t.provider && ` • ${t.provider}`}
                                                    </p>
                                                    {t.notes && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{t.notes}</p>}
                                                </div>
                                                <button onClick={() => handleDeleteTreatment(t.id)} className="opacity-0 group-hover:opacity-100 p-1">
                                                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Correlations */}
                    <HealthCorrelations />

                    {/* Symptom Calendar (v3.23) */}
                    <SymptomCalendar />

                    {/* Mood Calendar (v3.24) */}
                    <MoodCalendar />

                    {/* Mood ↔ Symptom Correlation (v3.25) */}
                    <MoodSymptomCorrelation />

                    {/* Symptom Analytics (v3.31) — per-symptom trends + correlations */}
                    <SymptomAnalytics />

                    {/* Prediction History (v3.34) — heatmap of Nanouk's hit/miss accuracy */}
                    <PredictionHistory />

                    {/* Body Composition Tracker (v3.39) — InBody/Fitdays parsing + trends */}
                    <BodyCompositionTracker />

                    {/* Lab Values Tracker (v3.56) — bloodwork / vitamins / hormones */}
                    <LabValuesTracker />

                    <SymptomHistoryList symptoms={symptoms} allSymptoms={allSymptoms} showHistory={showHistory} setShowHistory={setShowHistory} editingEntry={editingEntry} setEditingEntry={setEditingEntry} handleUpdateSymptom={handleUpdateSymptom} handleDeleteSymptom={handleDeleteSymptom} handleExportSymptomsCSV={handleExportSymptomsCSV} allSymptomsLoading={allSymptomsLoading} language={language} />
                </div>
            </motion.div>

            {/* Quick Log Drawer */}
            <AnimatePresence>
                {quickLogOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            onClick={() => setQuickLogOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border shadow-2xl max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex justify-center py-3 sticky top-0 bg-card">
                                <div className="w-12 h-1.5 bg-muted rounded-full" />
                            </div>
                            
                            <div className="px-4 pb-32 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                                        <HeartPulse className="w-5 h-5 text-rose-400" />
                                        {language === 'de' ? 'Symptom eintragen' : 'Log Symptom'}
                                    </h3>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setQuickLogOpen(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>

                                {/* Category Grid */}
                                <div>
                                    <Label className="text-muted-foreground mb-2 block">{language === 'de' ? 'Kategorie' : 'Category'}</Label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {categories.map((cat) => {
                                            const Icon = cat.icon;
                                            return (
                                                <button
                                                    key={cat.value}
                                                    onClick={() => setSelectedCategory(cat.value)}
                                                    className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${
                                                        selectedCategory === cat.value 
                                                            ? 'bg-secondary ring-2 ring-primary' 
                                                            : 'bg-card hover:bg-muted/50'
                                                    }`}
                                                >
                                                    <Icon className="w-5 h-5" style={{ color: cat.color }} />
                                                    <span className="text-xs text-muted-foreground">{cat.label[language]}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Symptom Selection */}
                                <div>
                                    <Label className="text-muted-foreground mb-2 block">{language === 'de' ? 'Symptom' : 'Symptom'}</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {symptomsByCategory[selectedCategory]?.map((sym) => (
                                            <button
                                                key={sym.value}
                                                onClick={() => { setSelectedSymptom(sym.value); setCustomSymptom(''); }}
                                                className={`px-3 py-2 rounded-full text-sm transition-all ${
                                                    selectedSymptom === sym.value && !customSymptom
                                                        ? 'bg-primary text-primary-foreground' 
                                                        : 'bg-secondary text-muted-foreground hover:bg-secondary'
                                                }`}
                                            >
                                                {sym.label[language]}
                                            </button>
                                        ))}
                                    </div>
                                    <ClearableInput
                                        value={customSymptom}
                                        onChange={(e) => { setCustomSymptom(e.target.value); if (e.target.value) setSelectedSymptom(''); }}
                                        placeholder={language === 'de' ? 'Oder eigenes Symptom eingeben...' : 'Or type your own symptom...'}
                                        className="bg-muted border-2 border-border h-10 text-sm mt-2"
                                        data-testid="custom-symptom-input"
                                        clearTestId="custom-symptom-clear"
                                    />
                                </div>

                                {/* Severity Slider */}
                                <div>
                                    <Label className="text-muted-foreground">{language === 'de' ? 'Schwere' : 'Severity'}: {severity}/10</Label>
                                    <Slider
                                        value={[severity]}
                                        onValueChange={([v]) => setSeverity(v)}
                                        min={1} max={10} step={1}
                                        className="py-4"
                                    />
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>😌 {language === 'de' ? 'Leicht' : 'Mild'}</span>
                                        <span>😐 {language === 'de' ? 'Mittel' : 'Moderate'}</span>
                                        <span>😣 {language === 'de' ? 'Stark' : 'Severe'}</span>
                                    </div>
                                </div>

                                {/* Notes with Voice */}
                                <div>
                                    <Label className="text-muted-foreground">{language === 'de' ? 'Notizen (optional)' : 'Notes (optional)'}</Label>
                                    <div className="flex gap-2 mt-1">
                                        <ClearableInput
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder={language === 'de' ? 'z.B. Nach dem Kaffee...' : 'e.g. After coffee...'}
                                            className="bg-secondary border-border"
                                            clearTestId="symptom-notes-clear"
                                        />
                                        <VoiceInput
                                            onResult={(text) => {
                                                setNotes(prev => {
                                                    const sep = prev && !prev.endsWith(' ') ? ' ' : '';
                                                    return (prev + sep + text).trimStart();
                                                });
                                                setNotesInterim('');
                                            }}
                                            onInterim={setNotesInterim}
                                            testid="symptom-notes-voice-btn"
                                        />
                                    </div>
                                    {notesInterim && (
                                        <div className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 text-xs" data-testid="symptom-notes-voice-interim">
                                            <span className="inline-flex h-2 w-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" aria-hidden />
                                            <span className="text-red-900 dark:text-red-200 italic truncate">{notesInterim}</span>
                                        </div>
                                    )}
                                </div>

                                <Button
                                    onClick={handleQuickLog}
                                    disabled={quickLoading}
                                    className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl"
                                >
                                    {quickLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Check className="w-5 h-5 mr-2" />
                                            {language === 'de' ? 'Speichern' : 'Save'}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* ==================== TREATMENT DRAWER ==================== */}
            <AnimatePresence>
                {treatmentDrawerOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.5 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black z-40"
                            onClick={() => setTreatmentDrawerOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border shadow-2xl max-h-[85vh] overflow-y-auto"
                        >
                            <div className="flex justify-center py-2">
                                <div className="w-12 h-1.5 bg-muted-foreground/20 rounded-full" />
                            </div>
                            
                            <div className="px-4 pb-32 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        <Stethoscope className="w-5 h-5 text-blue-500" />
                                        {language === 'de' ? 'Behandlung eintragen' : 'Log Treatment'}
                                    </h3>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setTreatmentDrawerOpen(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>

                                {/* Date */}
                                <div className="text-center py-1.5 px-3 rounded-xl bg-primary/10 text-primary text-sm font-medium">
                                    {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>

                                {/* Treatment Type Grid */}
                                <div className="grid grid-cols-4 gap-1.5">
                                    {treatmentTypes.map((type) => {
                                        const Icon = type.icon;
                                        return (
                                            <button
                                                key={type.value}
                                                onClick={() => setTreatmentForm({...treatmentForm, treatment_type: type.value})}
                                                className={`flex flex-col items-center gap-0.5 p-2 rounded-xl transition-all text-center ${
                                                    treatmentForm.treatment_type === type.value 
                                                        ? 'bg-secondary ring-2 ring-primary' 
                                                        : 'bg-card hover:bg-muted/50'
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" style={{ color: type.color }} />
                                                <span className="text-[9px] text-muted-foreground leading-tight">{type.label[language]}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Title */}
                                <ClearableInput
                                    value={treatmentForm.title}
                                    onChange={(e) => setTreatmentForm({...treatmentForm, title: e.target.value})}
                                    placeholder={language === 'de' ? 'Was wurde gemacht? (z.B. Akupunktur, Blutbild)' : 'What was done?'}
                                    className="bg-muted border-2 border-border h-11"
                                    data-testid="treatment-title"
                                    clearTestId="treatment-title-clear"
                                />

                                {/* Provider */}
                                <ClearableInput
                                    value={treatmentForm.provider}
                                    onChange={(e) => setTreatmentForm({...treatmentForm, provider: e.target.value})}
                                    placeholder={language === 'de' ? 'Arzt / Therapeut / Praxis' : 'Doctor / Therapist / Clinic'}
                                    className="bg-muted border-2 border-border h-10 text-sm"
                                    data-testid="treatment-provider"
                                    clearTestId="treatment-provider-clear"
                                />
                                <MemorySuggestions 
                                    category="treatment_provider"
                                    currentValue={treatmentForm.provider}
                                    onSelect={(s) => setTreatmentForm({...treatmentForm, provider: s.name, treatment_type: s.treatment_type || treatmentForm.treatment_type})}
                                />

                                {/* Notes */}
                                <ClearableInput
                                    value={treatmentForm.notes}
                                    onChange={(e) => setTreatmentForm({...treatmentForm, notes: e.target.value})}
                                    placeholder={language === 'de' ? 'Notizen (z.B. Befund, nächster Termin)' : 'Notes'}
                                    className="bg-muted border-2 border-border h-10 text-sm"
                                    data-testid="treatment-notes"
                                    clearTestId="treatment-notes-clear"
                                />

                                <Button
                                    onClick={handleSaveTreatment}
                                    disabled={treatmentLoading || !treatmentForm.title.trim()}
                                    className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-base font-bold"
                                    data-testid="treatment-save-btn"
                                >
                                    {treatmentLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Check className="w-5 h-5 mr-2" />
                                            {language === 'de' ? 'OK — Speichern' : 'OK — Save'}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
